import { Rarity } from "../data/balance";
import { GearItemJson, gearFromJson, gearToJson } from "./gear";
import { PlayerProfile } from "./profile";

/** What `salvageMany` took (the items as they were, never worn) and paid, for `restoreSalvaged`. */
export interface SalvageSnapshot {
  readonly items: readonly GearItemJson[];
  /** Gold paid. */
  readonly value: number;
  /** Forge Essence paid (gear v2). */
  readonly essence: number;
}

/**
 * Salvages the listed items (§9.6, Clear junk with per-item keep) through the profile's own salvage rule. Like
 * `bulkSalvage`, worn, locked and Mythic items are skipped (a list built from an old render cannot take them). Returns
 * what went and what it paid, which `restoreSalvaged` can put back (the Undo).
 */
export function salvageMany(profile: PlayerProfile, ids: readonly number[]): SalvageSnapshot {
  const items: GearItemJson[] = [];
  let value = 0, essence = 0;
  for (const id of ids) {
    const it = profile.findGear(id);
    if (it === null || it.rarity === Rarity.Mythic || it.equippedTo !== null || it.locked) continue;
    const json = gearToJson(it);
    const y = profile.salvageGear(id);
    if (typeof y === "string") continue;
    items.push(json);
    value += y.gold;
    essence += y.essence;
  }
  return { items, value, essence };
}

/**
 * Undoes a salvage: the items come back under their own ids, in id order, and the yield is paid back. The ids cannot
 * collide — the profile's id counter only grows, so no item made since holds one. Refused, changing nothing, when an
 * item is already back or the yield has been spent in the meantime (the Undo toast should close on any spend).
 */
export function restoreSalvaged(profile: PlayerProfile, snapshot: SalvageSnapshot): true | string {
  if (snapshot.items.some((j) => profile.findGear(j.id) !== null)) return "already restored";
  if (!profile.canAfford(snapshot.value)) return "gold already spent";
  if (profile.forge.essence < snapshot.essence) return "essence already spent";
  profile.gold -= snapshot.value;
  profile.forge.essence -= snapshot.essence;
  for (const j of snapshot.items) {
    const it = gearFromJson({ ...j, equippedTo: null });
    const at = profile.gear.findIndex((g) => g.id > it.id);
    profile.gear.splice(at < 0 ? profile.gear.length : at, 0, it);
  }
  return true;
}
